import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { App, Button, Tag, Tooltip } from 'antd'
import DataTableX from 'components/DataTableX/DataTableX'
import SelectForm from 'components/SelectForm/SelectForm'
import ButtonMenu from 'components/ButtonMenu/ButtonMenu'
import ModalRecurso from './components/ModalRecurso'
import Storage from 'util/store/Storage'
import { obtenerPermisosByPath } from 'util/funciones'
import { formatearFecha, formatosFecha } from 'util/formatDate'
import ModalConfirmacion from 'app/vistas/Tienda/Productos/components/ModalConfirmacionProduct'
import useRecursos from './hooks/useConfiguracionRecursos'
import useGenericInfo from 'hooks/useGenericInfo'
import { RecursosProvider } from './context/ConfiguracionRecursosContext'
import iconoInformacion from 'assets/images/datatable/informacion.svg'
import iconoEditar from 'assets/images/datatable/editar.svg'
import iconoInactivarActivar from 'assets/images/datatable/inactivar-activar.svg'
import { RecursoRow, ESTADOS_RECURSO, TIPOS_RECURSO } from './interfaces/ConfiguracionRecursos.interfaces'
import RecursosIcon from './assets/icono-recursos.svg'
import useTiposRecurso from './hooks/useTipoRecurso'
import { construirUrlRecurso } from './helpers/ModalRecursos.helper'

const estiloMiniatura: React.CSSProperties = {
    width: 72,
    height: 40,
    objectFit: 'contain',
    borderRadius: 4,
    background: '#fafafa',
    display: 'block',
}

const estiloEnlacePreview: React.CSSProperties = {
    display: 'inline-block',
    lineHeight: 0,
    border: '1px solid #f0f0f0',
    borderRadius: 6,
    padding: 2,
}

const ConfiguracionRecursosContent = () => {
    const { usuario } = Storage.getState()
    const location = useLocation()
    const permisos: string = useMemo(() => obtenerPermisosByPath(usuario, location.pathname), [usuario, location])

    const {
        organizationByUser,
        getEmpresasByOrganizacion,
        empresasByOrganizacion,
        resetEmpresasByOrganizacion,
    } = useGenericInfo({ organizations: true })

    const [filtros, setFiltros] = useState({
        organizacionSelected: null as number | null,
        empresaSelected: null as number | null,
        tipoSelected: null as number | null,
    })

    const [recursoSelected, setRecursoSelected] = useState<RecursoRow | null>(null)
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false)

    const {
        rows,
        loadingTable,
        openNew,
        openEdit,
        openView,
        fetchListado,
        toggleEstadoRecurso,
        setEmpresaFiltro,
    } = useRecursos()
    const { options: tiposOptions, loading: loadingTipos } = useTiposRecurso()
    const { notification } = App.useApp()

    const filteredRows = useMemo(
        () => (filtros.tipoSelected ? rows.filter((r) => r.tipo_id === filtros.tipoSelected) : rows),
        [rows, filtros.tipoSelected]
    )

    const nombreEstacion = (record: RecursoRow) =>
        record.empresa_nombre ?? (record.empresa_id ? `Estación ${record.empresa_id}` : null)

    const mostrarColumnaEstacion = useMemo(() => rows.some((row) => nombreEstacion(row) !== null), [rows])

    const onChangeOrganizacion = (value?: number | null) => {
        setFiltros({ ...filtros, organizacionSelected: value ?? null, empresaSelected: null })
        setEmpresaFiltro(null)
        if (value) getEmpresasByOrganizacion(value)
        else resetEmpresasByOrganizacion()
    }

    const onChangeEmpresa = (value?: number | null) => {
        setFiltros({ ...filtros, empresaSelected: value ?? null })
        setEmpresaFiltro(value ?? null)
    }

    const renderButtonMenu = (record: RecursoRow) => [
        {
            label: 'Información',
            icon: <img src={iconoInformacion} />,
            onClick: () => openView(record),
        },
        {
            label: 'Editar',
            icon: <img src={iconoEditar} />,
            onClick: () => openEdit(record),
        },
        {
            label: record.estado_id === ESTADOS_RECURSO.INACTIVO ? 'Activar' : 'Inactivar',
            icon: <img src={iconoInactivarActivar} />,
            onClick: () => {
                setRecursoSelected(record)
                setOpenModalConfirm(true)
            },
            disabled: !permisos.includes('D'),
        },
    ]

    const COLUMNAS_RECURSOS: any = [
        {
            dataIndex: 'id',
            align: 'center',
            title: 'ID',
            render: (value: number) => <span className="mono">{value}</span>,
        },
        {
            key: 'vista_previa',
            dataIndex: 'url',
            align: 'center',
            title: 'Vista previa',
            render: (value: string, record: RecursoRow) => {
                if (!value) return '-'
                return (
                    <Tooltip title="Abrir recurso en una pestaña nueva">
                        <a href={value} target="_blank" rel="noopener noreferrer" style={estiloEnlacePreview}>
                            {record.tipo_id === TIPOS_RECURSO.VIDEO ? (
                                <video src={value} preload="metadata" muted style={estiloMiniatura} />
                            ) : (
                                <img src={value} alt={record.nombre} style={estiloMiniatura} />
                            )}
                        </a>
                    </Tooltip>
                )
            },
        },
        {
            dataIndex: 'orden',
            align: 'center',
            title: 'Orden',
            sorter: (a: RecursoRow, b: RecursoRow) => (a.orden ?? 0) - (b.orden ?? 0),
            render: (value: number | null) => value ?? '-',
        },
        { dataIndex: 'nombre', align: 'center', title: 'Nombre' },
        {
            dataIndex: 'descripcion',
            align: 'center',
            title: 'Descripción',
            ellipsis: true,
            render: (value: string | null) =>
                value ? <Tooltip title={value}>{value}</Tooltip> : '-',
        },
        ...(mostrarColumnaEstacion
            ? [
                  {
                      dataIndex: 'empresa_nombre',
                      align: 'center',
                      title: 'Estación',
                      render: (_: any, record: RecursoRow) => nombreEstacion(record) ?? '-',
                  },
              ]
            : []),
        {
            dataIndex: 'tipo_nombre',
            align: 'center',
            title: 'Tipo',
            render: (value: string, record: RecursoRow) => (
                <Tag color={record.tipo_id === TIPOS_RECURSO.IMAGEN ? 'blue' : 'purple'}>{value}</Tag>
            ),
        },
        {
            dataIndex: 'fecha_creacion',
            align: 'center',
            title: 'Fecha Creación',
            render: (value: string) => formatearFecha(value, formatosFecha.completaServidor),
        },
        {
            dataIndex: 'fecha_modificacion',
            align: 'center',
            title: 'Fecha Modificación',
            render: (value: string | null) => (value ? formatearFecha(value, formatosFecha.completaServidor) : '-'),
        },
        {
            dataIndex: 'estado_id',
            align: 'center',
            title: 'Estado',
            render: (value: number) => (
                <Tag color={value === ESTADOS_RECURSO.ACTIVO ? 'success' : 'error'}>
                    {value === ESTADOS_RECURSO.ACTIVO ? 'Activo' : 'Inactivo'}
                </Tag>
            ),
        },
        {
            dataIndex: 'acciones',
            align: 'center',
            title: 'Acciones',
            render: (_: any, record: RecursoRow) => (
                <section style={{ width: '100%', display: 'flex', justifyContent: 'center' }} className="w-100">
                    <ButtonMenu itemsMenu={renderButtonMenu(record)} />
                </section>
            ),
        },
    ]

    const handleConfirmAction = async (record: RecursoRow | null) => {
        if (!record) return
        const result = await toggleEstadoRecurso(record)
        setOpenModalConfirm(false)
        setRecursoSelected(null)

        if (result.success) {
            notification.success({ message: 'Éxito', description: result.message })
            return
        }

        notification.warning({
            message: 'Advertencia',
            description: result.message ?? 'No se pudo actualizar el estado del recurso.',
        })

        if ('requiereArchivo' in result && result.requiereArchivo) openEdit(record)
    }

    useEffect(() => {
        fetchListado()
    }, [fetchListado])

    return (
        <div>
            <ModalConfirmacion
                logo={<RecursosIcon />}
                confirmText="SÍ"
                message={
                    recursoSelected?.estado_id === ESTADOS_RECURSO.INACTIVO
                        ? '¿Está seguro de que desea activar este recurso?'
                        : '¿Está seguro de que desea inactivar este recurso? Se eliminará el archivo y, para volver a activarlo, tendrá que cargar uno nuevo desde la edición.'
                }
                openModal={openModalConfirm}
                onClose={() => setOpenModalConfirm(false)}
                onConfirm={() => handleConfirmAction(recursoSelected)}
            />
            <DataTableX
                iconTable={<img src={RecursosIcon} alt="icono" className="icon-table" />}
                titleTable="Recursos"
                nameReport="recursos"
                loadingTable={loadingTable}
                data={filteredRows}
                columns={COLUMNAS_RECURSOS}
                extraFilter={
                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <SelectForm
                            style={{ flex: 1 }}
                            allowClear
                            placeholder="Seleccione su organización"
                            options={organizationByUser}
                            onChange={onChangeOrganizacion}
                            value={filtros.organizacionSelected}
                        />
                        {filtros.organizacionSelected && (
                            <SelectForm
                                style={{ flex: 1 }}
                                allowClear
                                placeholder="TODAS LAS ESTACIONES"
                                options={empresasByOrganizacion}
                                onChange={onChangeEmpresa}
                                value={filtros.empresaSelected}
                            />
                        )}
                        <SelectForm
                            style={{ flex: 1 }}
                            allowClear
                            placeholder="TODOS LOS TIPOS"
                            options={tiposOptions}
                            onChange={(value?: number | null) => setFiltros({ ...filtros, tipoSelected: value ?? null })}
                            value={filtros.tipoSelected}
                        />
                    </div>
                }
                extraHeader={
                    <div className="section-extra-buttons" style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <Button
                            type="primary"
                            className="btn-new"
                            style={{ borderRadius: '12px', width: '100%', color: 'black', fontWeight: 'bold' }}
                            onClick={openNew}
                        >
                            +Nuevo Recurso
                        </Button>
                    </div>
                }
            />

            <ModalRecurso />
        </div>
    )
}

const ConfiguracionRecursos = () => (
    <RecursosProvider>
        <ConfiguracionRecursosContent />
    </RecursosProvider>
)

export default ConfiguracionRecursos